import { TOKEN_BLACKLIST } from 'config/constants/info'
import { gql } from 'graphql-request'
import { useEffect, useState, useRef } from 'react'
import { infoClient } from 'utils/graphql'
import { getDeltaTimestamps } from 'views/Info/utils/infoQueryHelpers'

interface TopTokensResponse {
  tokens: {
    id: string
  }[]
}

/**
 * Fetch all tokens - removed restrictive filters to show all tokens
 */
const fetchTopTokens = async (timestamp24hAgo: number): Promise<string[]> => {
  try {
    // Query tokens directly instead of tokenDayDatas to get all tokens, not just recent ones
    const query = gql`
      query topTokens($blacklist: [String!]) {
        tokens(
          first: 1000
          where: { id_not_in: $blacklist }
          orderBy: tradeVolumeUSD
          orderDirection: desc
        ) {
          id
        }
      }
    `
    const data = await infoClient.request<TopTokensResponse>(query, { blacklist: TOKEN_BLACKLIST })
    return data.tokens.map((t) => t.id)
  } catch (error) {
    console.error('Failed to fetch top tokens', error)
    // Fallback to tokenDayDatas if tokens query fails
    try {
      const fallbackQuery = gql`
        query topTokensFallback($blacklist: [String!], $timestamp24hAgo: Int) {
          tokenDayDatas(
            first: 1000
            where: { date_gt: $timestamp24hAgo }
            orderBy: dailyVolumeUSD
            orderDirection: desc
          ) {
            id
          }
        }
      `
      const fallbackData = await infoClient.request<{ tokenDayDatas: { id: string }[] }>(fallbackQuery, {
        blacklist: TOKEN_BLACKLIST,
        timestamp24hAgo,
      })
      return fallbackData.tokenDayDatas.map((t) => t.id.split('-')[0])
    } catch (fallbackError) {
      console.error('Failed to fetch tokens with fallback query', fallbackError)
      return []
    }
  }
}

/**
 * Fetch top addresses by volume
 */
const useTopTokenAddresses = (): string[] => {
  const [topTokenAddresses, setTopTokenAddresses] = useState<string[]>([])
  const [timestamp24hAgo] = getDeltaTimestamps()
  const lastFetchedTimestamp = useRef<number | null>(null)

  useEffect(() => {
    // Only fetch if we haven't fetched for this timestamp yet
    if (lastFetchedTimestamp.current !== timestamp24hAgo) {
      const fetch = async () => {
        try {
          const addresses = await fetchTopTokens(timestamp24hAgo)
          setTopTokenAddresses(addresses)
          lastFetchedTimestamp.current = timestamp24hAgo
        } catch (error) {
          console.error('Error in useTopTokenAddresses fetch:', error)
          // Still mark as fetched to prevent infinite retries
          lastFetchedTimestamp.current = timestamp24hAgo
        }
      }
      fetch()
    }
  }, [timestamp24hAgo])

  return topTokenAddresses
}

export default useTopTokenAddresses
